import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { Qualification } from 'shell/models/qualification';
import FormQualification, { QualificationForm } from 'system/components/qualifications/form';
import ListOperator from 'system/components/qualifications/operators';
import { CommonQualificationCrumbs } from '../constants';

const QualificationEditPage = (): React.ReactElement => {
  const params = useParams();
  const {
    put: updateQualification,
    data,
    get: getQualification,
    loading,
    error,
  } = useHttpFetch<HttpResponse<Qualification>>(
    `${__MANAGEMENT_API_URL__}/qualifiche/${params.id}`
  );

  const qualificationInitialValues: QualificationForm = useMemo(() => {
    const qualification = data?.payload;
    if (!qualification || !!error) {
      return {
        code: '',
        name: '',
      };
    }
    return {
      code: qualification.key?.codiceQualifica,
      name: qualification.descrizione,
      lastUpdate: qualification.note,
    };
  }, [data]);

  const navigate = useNavigate();
  const onSubmit = useCallback(
    (formValues) => {
      updateQualification({
        descrizione: formValues.name,
        note: formValues.lastUpdate,
        key: {
          codiceQualifica: formValues.code,
        },
      }).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/sistema/qualifiche');
      });
    },
    [updateQualification, navigate]
  );

  useEffect(() => {
    getQualification();
  }, [params.id]);

  const onDetailPress = (idOperatore: string) => {
    navigate(`/sistema/qualifiche/modifica/${params.id}/operatori/dettaglio/${idOperatore}`);
    return;
  };

  const onEditPress = (idOperatore: string) => {
    navigate(`/sistema/qualifiche/modifica/${params.id}/operatori/modifica/${idOperatore}`);
    return;
  };

  return (
    <Fragment>
      <Breadcrumbs items={[...CommonQualificationCrumbs, { name: 'Modifica Qualifica' }]} />
      {loading && !data ? null : (
        <FormQualification
          title="Modifica Qualifica"
          onOk={onSubmit}
          mode="EDIT"
          onCancel={() => {
            navigate('/sistema/qualifiche');
          }}
          initialValues={qualificationInitialValues}
        >
          <ListOperator
            isEdit={true}
            onDetailPress={onDetailPress}
            onEditPress={onEditPress}
            qualificationId={qualificationInitialValues.code}
          />
        </FormQualification>
      )}
    </Fragment>
  );
};

QualificationEditPage.displayName = 'Qualification Edit page component';

export default QualificationEditPage;
